import { CarsStore } from 'stores/cars-store';
import { action, observable, runInAction } from 'mobx';
import { CarDropOffSearch, ContextInput } from 'common/__generated__/api';
import { ContextStore, AnalyticsStore } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { SystemEvent, getErrorString } from 'bernie-logger';
import { STORE_EVENTS } from 'stores/store-event';
import analyticsUtil from 'utils/analytics-util';
import { DropOffCriteriaStore } from 'stores/drop-off-criteria-store';

export class CarDropOffStore extends CarsStore {
  private context: ContextInput;

  @observable
  public dropOffSearchResult: CarDropOffSearch.CarDropOffSearch;

  public dropOffTitle: CarDropOffSearch.CarDropOffTitle;

  public dropOffHeader: string;

  @observable
  public dropOffOffers: CarDropOffSearch.CarDropOffOfferCard[];

  @observable
  public map: CarDropOffSearch.Map;

  @observable
  public isLoading: boolean = true;

  @observable
  public errorInFetchingResult: boolean = false;

  @observable
  public isRequestFromSearch: boolean = false;

  @observable
  public srpQueryParams: string;

  @observable
  public timeOutCompleted: boolean;

  @observable
  public detailsRedirectUrl: string;

  public constructor(bexConfigs, logger, appconfig: SerializedData, param) {
    super(bexConfigs, logger, appconfig, param);
    this.logger = logger;
  }

  public failureHandler(variable: any, error: any, event: SystemEvent): void {
    this.logger.logEvent(event, variable, getErrorString(error));
  }

  public setContext(context: ContextStore): void {
    this.context = getBexApiContext(context);
  }

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
    this.context = { ...data.context };
  }

  @action
  public getDropOffResult(
    dropOffCriteriaStore: DropOffCriteriaStore,
    pageId: string,
    analytics: AnalyticsStore,
    domainServiceRedirect: boolean
  ): Promise<any> {
    const searchVariables: CarDropOffSearch.Variables = dropOffCriteriaStore.dropOffVariables;

    this.bexApiSource.headers = this.addDomainRedirectHeader(this.bexApiSource.headers, domainServiceRedirect);

    return this.bexApiSource.operations
      .carDropOffSearch(searchVariables, {
        pageId
      })
      .then(response => {
        runInAction('Car Drop off Search Results', () => {
          this.startResultsExpirationTimer(() => (this.timeOutCompleted = true));
          const responseData = response.data;
          if (responseData) {
            const { carDropOffSearch } = responseData;
            this.dropOffSearchResult = carDropOffSearch;
            this.dropOffTitle = this.dropOffSearchResult.carDropOffTitle;
            this.dropOffHeader = document.title = this.dropOffSearchResult.carDropOffOfferCardTitle;
            this.dropOffOffers = this.dropOffSearchResult.carDropOffOfferCard;
            this.map = this.dropOffSearchResult.map;
            this.errorInFetchingResult = false;

            // Redirect to details in case we have got only one drop of location
            this.detailsRedirectUrl =
              this.dropOffSearchResult && this.dropOffOffers && this.dropOffOffers.length === 1
                ? this.dropOffOffers[0].selectButton.url.relativePath
                : null;
          } else {
            // For any known errors that comes from server and sometimes if there is an issue with spinner
            this.errorInFetchingResult = true;
          }
          analyticsUtil.publishAnalyticsPayload(response, analytics);
        });
      })
      .catch(error => {
        runInAction('recovering from bex api car drop off availability call breakdown', () => {
          this.dropOffSearchResult = null;
          this.errorInFetchingResult = true;
          this.failureHandler(searchVariables, error, STORE_EVENTS.DROP_OFF_AVAILABILITY_FETCH_FAILED);
        });
        throw error;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  public resetDropOffStore(searchQueryParams: string) {
    this.dropOffSearchResult = null;
    this.dropOffTitle = null;
    this.dropOffHeader = null;
    this.dropOffOffers = null;
    this.map = null;
    this.srpQueryParams = searchQueryParams;
    this.isLoading = true;
    this.isRequestFromSearch = true;
    this.detailsRedirectUrl = null;
    this.errorInFetchingResult = false;
  }
}
